import { generatedStairBlockCentroid } from "building-generation/elements/stairSystem/stairTools";
import type { BuildingGeometryEmitter } from "building-types/geometry/buildingGeometryTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { LevelExtrusion } from "building-types/levels/levelPerimeters";
import type { Level } from "building-types/levels/levelTypes";
import type { ExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import { FaceCategory } from "building-types/materials/faceCategories";
import type { MaterialSrcOverride } from "building-types/materials/materialTypes";
import type { GeneratedStairBlock, GeneratedStairSystem } from "building-types/stairs/stairTypes";
import type { Table } from "common/data/table";
import { materialSrcFromPalette } from "doc/materialLookup";
import { geomAABB3 } from "geom/geom";
import type GeomTagGroup from "geom/geomTagGroup";
import centroidInsideLevelExtrusions from "../utils/centroidInsideLevelExtrusions";
import { buildExteriorLayers, buildInteriorLayers } from "./buildingGeometryTools";

function stairBlockExtendsBelowElevation(
  geomGroups: GeomTagGroup<MaterialSrcOverride>,
  elevationThreshold: number,
): boolean {
  for (const geom of geomGroups.geom) {
    const aabb = geomAABB3(geom);
    if (aabb.minZ < elevationThreshold) {
      return true;
    }
  }
  return false;
}

export function getStairSystemGeometries(
  stairSystems: Table<GeneratedStairSystem>,
  levelExtrusions: Table<LevelExtrusion> | undefined,
  levels: Table<Level>,
  exteriorPalette: ExteriorPalette,
  emitter: BuildingGeometryEmitter,
): void {
  const defaultMaterialSrc = materialSrcFromPalette(exteriorPalette, "trimExterior0");

  for (const stairSystemId in stairSystems) {
    const stairSystem = stairSystems[stairSystemId];
    const { blocks, levelId, instanceId } = stairSystem;
    emitter.beginGroup?.("Stair", stairSystemId);

    let entityHandleForSelectingReportingUnit: DocEntityHandle | undefined;
    if (instanceId) {
      entityHandleForSelectingReportingUnit = { type: "COMPONENT_INSTANCE", id: instanceId };
    } else {
      entityHandleForSelectingReportingUnit = { type: "STAIR_SYSTEM", id: stairSystemId };
    }

    for (const blockId in blocks) {
      const block: GeneratedStairBlock = blocks[blockId];
      const { geom, pickHandle } = block;
      const { geom: geomArray, tags } = geom;

      // In case stairs extend far down from the floor elevation, don't associate them with a level.
      // We want stairs that extend downwards to be visible even if their level is hidden.
      const elevationThreshold = levels[levelId].floorElevation - 4;
      const associateStairBlockWithLevel = !stairBlockExtendsBelowElevation(geom, elevationThreshold);

      let exteriorLayers = true;
      if (levelExtrusions) {
        // A degenerate sub-geometry makes the centroid NaN; treat that as interior rather than
        // letting it silently light the whole stair block as an exterior object.
        exteriorLayers = !(centroidInsideLevelExtrusions(levelExtrusions, generatedStairBlockCentroid(block)) ?? true);
      }

      for (let i = 0, l = geomArray.length; i < l; i++) {
        const geo = geomArray[i];
        const { materialSrc = defaultMaterialSrc, uvGen, viewEntityDescriptor, exportComponentName } = tags[i];

        emitter.emit({
          geom: geo,
          faceCategory: FaceCategory.STAIR,
          levelId: associateStairBlockWithLevel ? levelId : undefined,
          pickHandle,
          materialSlot: tags[i].materialSrc === undefined ? "trimExterior0" : undefined,
          materialSrc,
          uvGen,
          entityId: viewEntityDescriptor?.entityId,
          entityHandleForSelectingReportingUnit,
          descriptor: viewEntityDescriptor,
          exportComponentName,
          threeLayers: exteriorLayers ? buildExteriorLayers() : buildInteriorLayers(),
        });
      }
    }

    emitter.endGroup?.("Stair");
  }
}
